'use client';

import { useTheme } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { http } from '../../lib/api/http';
import { V3 } from '../../lib/api/urls';
import { activeStatus } from '../../constants/events';
import { Container } from '../layout/Container';
import { DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import { City, Action } from '../../models';
import { Posts } from '../posts/posts';
import { getEvents } from '../../lib/services/backend';

export function NotFoundComponent({ currentCity }: { currentCity: City }) {
  const theme = useTheme();
  const router = useRouter();

  useEffect(() => {
    document.title = '404 — Страница не найдена | Горбилет';
  }, []);

  const [recommends, setRecommends] = useState<{
    actions: { results: Action[] };
    slug: string;
  } | null>(null);

  useEffect(() => {
    async function fetch() {
      const compilationsData = await http.get(V3.compilations.root, {
        city: currentCity.id,
        is_gb_recommended: true,
      });
      const data = await getEvents({
        ids: compilationsData.results[0].events.join(','),
        is_web: true,
        status: activeStatus,
        limit: 8,
        city: currentCity.id,
      });

      setRecommends({
        actions: data,
        slug: compilationsData.results[0].slug,
      });
    }
    fetch();
  }, []);

  return (
    <Container
      css={{
        padding: '0 8px',
        [DEVICE_SCREENS_MAX_STRING.md]: {
          padding: '32px 8px',
        },
      }}
      className='container'
    >
      <div
        css={{
          fontFamily: 'Golos',
          color: theme.colors.black,
          position: 'relative',
          minHeight: 560,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 40,
          [DEVICE_SCREENS_MAX_STRING.md]: {
            minHeight: 430,
          },
        }}
      >
        <h1
          css={{
            fontSize: 48,
            fontWeight: 800,
            textAlign: 'center',
            [DEVICE_SCREENS_MAX_STRING.md]: {
              fontSize: 30,
              fontWeight: 700,
            },
          }}
        >
          Страница не найдена
        </h1>
        <div
          css={{
            fontSize: 14,
            fontWeight: 500,
            textAlign: 'center',
          }}
        >
          «404… Увы, но эта страница где-то затерялась в галактике Интернета»
        </div>
        <div
          css={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 288,
            fontWeight: 700,
            letterSpacing: 30,
            opacity: 0.1,
            color: theme.colors.secondary,
            [DEVICE_SCREENS_MAX_STRING.md]: {
              fontSize: 130,
              letterSpacing: 10,
            },
          }}
        >
          404
        </div>
        <button
          type='button'
          onClick={() => router.push(`/${currentCity.url}/`)}
          css={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            marginTop: 190,
            height: 45,
            backgroundColor: 'transparent',
            borderRadius: 4,
            padding: '0 0.8em',
            fontSize: 14,
            fontWeight: 500,
            border: '2px solid #f44336',
            cursor: 'pointer',
            textDecoration: 'none',
            color: theme.colors.black,
            lineHeight: '41px',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            transition: 'all 0.2s ease',
            ':hover': {
              backgroundColor: theme.colors.red,
              color: theme.colors.white,
            },
            [DEVICE_SCREENS_MAX_STRING.md]: {
              marginTop: 120,
            },
          }}
        >
          На главную
        </button>
      </div>
      {recommends && recommends.actions.results?.length > 0 && (
        <Posts
          title='Вам может быть интересно'
          actions={recommends.actions}
          href={['', currentCity.url, 'compilations', recommends.slug, ''].join(
            '/'
          )}
        />
      )}
    </Container>
  );
}
